import { useCallback } from "react";
import {
  ApolloError,
  LazyQueryHookOptions,
  QueryHookOptions,
  QueryResult,
  useLazyQuery as useApolloLazyQuery,
} from "@apollo/client";

import { Query, SchemaType } from "base-types";

import { parseApolloError } from "./errors";
import { useGraphQLClient } from "./GraphQLClientContext";
import { WithOperation } from "./types";
import { getOutput } from "./utils";

export const useLazyQuery = <
  Data,
  Variables,
  VariablesRequired,
  Schema extends SchemaType,
>(
  query: Query<Data, Variables, VariablesRequired, Schema>,
  options?: QueryHookOptions<Data, Variables>,
): [
  (
    runtimeOptions: LazyQueryHookOptions<Data, Variables>,
  ) => Promise<QueryResult<Data, Variables>>,
  QueryResult<Data, Variables>,
] => {
  const client = useGraphQLClient().graphQLClients[query.schemaType];
  const mergedOptions = {
    client,
    ...options,
  };

  const [execute, { data, ...queryResult }] = useApolloLazyQuery(
    query.document,
    mergedOptions,
  );

  return [
    useCallback(
      (runtimeOptions) =>
        execute(runtimeOptions)
          .then((result) => ({
            ...result,
            data: getOutput(result.data as WithOperation<Data>),
          }))
          .catch((e: ApolloError) => {
            throw parseApolloError(e);
          }),
      [execute],
    ),
    {
      ...queryResult,
      data: getOutput(data as WithOperation<Data>),
    },
  ];
};
